import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, CheckSquare, Square, Building2 } from 'lucide-react';
import useFusionPtrStore from '../../stores/useFusionPtrStore';
import SnapshotCard from './SnapshotSections';

// Fullscreen details modal for one analysis (portal to document.body — see
// docs/technical/Modal_Fullscreen_Fix.md). Fetches the analysis snapshot on
const AnalysisDetailModal = ({ analysisId = null, canSelect = true, onClose }) => {
  const { t } = useTranslation();
  const analysis = useFusionPtrStore((s) => (s.analyses || []).find((a) => String(a._id || a.id) === String(analysisId)));
  const snapshot = useFusionPtrStore((s) => s.snapshotCache[String(analysisId)]);
  const loading = useFusionPtrStore((s) => !!s.snapshotLoading[String(analysisId)]);
  const loadError = useFusionPtrStore((s) => s.snapshotErrors[String(analysisId)] || null);
  const ensureSnapshot = useFusionPtrStore((s) => s.ensureSnapshot);
  const selectedIds = useFusionPtrStore((s) => s.selectedIds);
  const toggleSelect = useFusionPtrStore((s) => s.toggleSelect);

  useEffect(() => {
    if (analysisId) ensureSnapshot(analysisId);
  }, [analysisId, ensureSnapshot]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!analysisId) return null;
  const selected = selectedIds.includes(String(analysisId));

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={analysis?.name || ''}>
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-slate-100 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start gap-3 px-5 py-4 bg-white border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-slate-800 truncate">{analysis?.name || t('fusionPtr.detailsTitle', 'Détail de l\u2019analyse')}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
              {analysis?.status && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                  {analysis.status}
                </span>
              )}
              {analysis?.companyName && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium max-w-[200px]">
                  <Building2 size={10} className="flex-shrink-0" />
                  <span className="truncate">{analysis.companyName}</span>
                </span>
              )}
            </div>
          </div>
          {canSelect && analysis && (
            <button
              type="button"
              onClick={() => toggleSelect(String(analysis._id || analysis.id))}
              aria-pressed={selected}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                selected
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {selected ? <CheckSquare size={14} /> : <Square size={14} />}
              {selected
                ? t('fusionPtr.selected', 'Sélectionnée')
                : t('fusionPtr.selectForFusion', 'Sélectionner pour la fusion')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            autoFocus
            aria-label={t('common.close', 'Fermer')}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && !snapshot ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6">
              <Loader2 size={16} className="animate-spin" />
              {t('fusionPtr.loadingSnapshot', 'Chargement du périmètre, des biens supports et des mesures...')}
            </div>
          ) : loadError && !snapshot ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertTriangle size={14} />
              <span className="flex-1">{loadError}</span>
              <button type="button" onClick={() => ensureSnapshot(analysisId)} className="font-semibold hover:underline">
                {t('common.retry', 'Réessayer')}
              </button>
            </div>
          ) : snapshot ? (
            <SnapshotCard snap={snapshot} />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

AnalysisDetailModal.propTypes = {
  analysisId: PropTypes.string,
  canSelect: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default AnalysisDetailModal;
